import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFlaskVial, faDroplet, faChartBar, faListUl } from '@fortawesome/free-solid-svg-icons';

const MetabolomicReport = ({ id, type, data, source }) => {
    // Determine if this is urine or plasma
    const isUrine = source === 'Metabolomic Urine' || type === 'Metabolomic Urine';
    const isPlasma = source === 'Metabolomic Plasma' || type === 'Metabolomic Plasma';
    
    // Placeholder data
    const metabolomicData = data || {
        summary: {
            totalMetabolites: isUrine ? 487 : 612,
            abnormalMetabolites: isUrine ? 28 : 35,
            percentileRank: isUrine ? 68 : 72
        },
        keyMetabolites: isUrine ? [
            { name: 'Creatinine', level: 'Normal', value: 1.2, unit: 'g/L' },
            { name: 'Urea', level: 'Normal', value: 20.1, unit: 'g/L' },
            { name: 'Citrate', level: 'Low', value: 0.15, unit: 'g/L' },
            { name: 'Oxalate', level: 'High', value: 0.04, unit: 'g/L' },
            { name: 'Hippurate', level: 'Normal', value: 0.38, unit: 'g/L' }
        ] : [
            { name: 'Glucose', level: 'Normal', value: 5.2, unit: 'mmol/L' },
            { name: 'Lactate', level: 'Normal', value: 1.8, unit: 'mmol/L' },
            { name: 'Pyruvate', level: 'High', value: 0.15, unit: 'mmol/L' },
            { name: 'Citrate', level: 'Low', value: 0.08, unit: 'mmol/L' },
            { name: 'Alanine', level: 'Normal', value: 0.42, unit: 'mmol/L' }
        ],
        pathwayAnalysis: isUrine ? [
            { pathway: 'Amino Acid Metabolism', status: 'Normal', score: 82 },
            { pathway: 'TCA Cycle', status: 'Altered', score: 68 },
            { pathway: 'Purine Metabolism', status: 'Normal', score: 79 },
            { pathway: 'Gut Microbiome Metabolites', status: 'Altered', score: 65 }
        ] : [
            { pathway: 'Glycolysis', status: 'Normal', score: 85 },
            { pathway: 'TCA Cycle', status: 'Altered', score: 72 },
            { pathway: 'Fatty Acid Metabolism', status: 'Normal', score: 88 },
            { pathway: 'Amino Acid Metabolism', status: 'Altered', score: 76 }
        ],
        recommendations: isUrine ? [
            'Increase water intake to improve kidney function',
            'Consider citrate supplementation to prevent kidney stone formation',
            'Reduce oxalate-rich foods in diet'
        ] : [
            'Maintain balanced diet rich in antioxidants',
            'Consider supplementation with B-vitamins to support TCA cycle',
            'Regular exercise to improve metabolic health'
        ]
    };

    return (
        <div className="OmicsView-metabolomic-report">
            <h3>
                {isUrine ? (
                    <><FontAwesomeIcon icon={faFlaskVial} /> Urine Metabolomic Report</>
                ) : isPlasma ? (
                    <><FontAwesomeIcon icon={faDroplet} /> Plasma Metabolomic Report</>
                ) : (
                    <>Metabolomic Report</>
                )} for {id}
            </h3>
            <div className="OmicsView-metabolomic-content">
                <div className="OmicsView-metabolomic-section">
                    <h4>Metabolite Profile Summary</h4>
                    <div className="OmicsView-metabolomic-summary">
                        <div className="OmicsView-metabolomic-summary-item">
                            <div className="OmicsView-metabolomic-summary-value">
                                {metabolomicData.summary.totalMetabolites}
                            </div>
                            <div className="OmicsView-metabolomic-summary-label">
                                Total Metabolites Detected
                            </div>
                        </div>
                        <div className="OmicsView-metabolomic-summary-item">
                            <div className="OmicsView-metabolomic-summary-value highlight">
                                {metabolomicData.summary.abnormalMetabolites}
                            </div>
                            <div className="OmicsView-metabolomic-summary-label">
                                Abnormal Metabolites
                            </div>
                        </div>
                        <div className="OmicsView-metabolomic-summary-item">
                            <div className="OmicsView-metabolomic-summary-percentile">
                                <div className="OmicsView-metabolomic-percentile-bar">
                                    <div 
                                        className="OmicsView-metabolomic-percentile-fill"
                                        style={{ width: `${metabolomicData.summary.percentileRank}%` }}
                                    ></div>
                                </div>
                                <div className="OmicsView-metabolomic-percentile-value">
                                    {metabolomicData.summary.percentileRank}%
                                </div>
                            </div>
                            <div className="OmicsView-metabolomic-summary-label">
                                Metabolic Health Percentile
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="OmicsView-metabolomic-section">
                    <h4>
                        <FontAwesomeIcon icon={faChartBar} /> Key Metabolites
                    </h4>
                    <div className="OmicsView-metabolomic-metabolites">
                        <table className="OmicsView-metabolomic-table">
                            <thead>
                                <tr>
                                    <th>Metabolite</th>
                                    <th>Level</th>
                                    <th>Value</th>
                                    <th>Unit</th>
                                </tr>
                            </thead>
                            <tbody>
                                {metabolomicData.keyMetabolites.map((metabolite, index) => (
                                    <tr key={index}>
                                        <td>{metabolite.name}</td>
                                        <td>
                                            <span className={`level-${metabolite.level.toLowerCase()}`}>
                                                {metabolite.level}
                                            </span>
                                        </td>
                                        <td>{metabolite.value}</td>
                                        <td>{metabolite.unit}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                
                <div className="OmicsView-metabolomic-section">
                    <h4>Metabolic Pathway Analysis</h4>
                    <div className="OmicsView-metabolomic-pathways">
                        {metabolomicData.pathwayAnalysis.map((pathway, index) => (
                            <div key={index} className="OmicsView-metabolomic-pathway">
                                <div className="OmicsView-metabolomic-pathway-name">
                                    {pathway.pathway}
                                </div>
                                <div className="OmicsView-metabolomic-pathway-bar-container">
                                    <div 
                                        className={`OmicsView-metabolomic-pathway-bar status-${pathway.status.toLowerCase()}`}
                                        style={{ width: `${pathway.score}%` }}
                                    ></div>
                                </div>
                                <div className="OmicsView-metabolomic-pathway-score">
                                    {pathway.score}/100
                                </div>
                                <div className={`OmicsView-metabolomic-pathway-status status-${pathway.status.toLowerCase()}`}>
                                    {pathway.status}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                
                <div className="OmicsView-metabolomic-section">
                    <h4>
                        <FontAwesomeIcon icon={faListUl} /> Recommendations
                    </h4>
                    <ul className="OmicsView-metabolomic-recommendations">
                        {metabolomicData.recommendations.map((recommendation, index) => (
                            <li key={index}>{recommendation}</li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default MetabolomicReport;
